import { useEffect, useState } from "react";
import type { ClaudeConnectionDTO, ConnectionDTO } from "../../shared/types";
import { BrandTile } from "../components/BrandIcon";
import { List, ListRow } from "../components/List";
import { Dialog } from "../components/Dialog";
import { Badge, Button, ErrorText, Field, inputCls, PageHeader, shortDate } from "../components/ui";
import { del, get, post, put } from "../lib/api";
import { useLocation } from "../lib/router";

/** A sign-in finished in another tab tells this page on this channel; the page also reloads when it gets the focus back. */
const CHANNEL = "agentos-connections";

const GROUPS = [
  { key: "needs_you", title: "Needs you", hint: "Tools that use your own login. Agents working for you cannot use them until you connect." },
  { key: "connected", title: "Connected", hint: "Agents working for you use these with exactly the access you have there, and never anyone else's." },
  { key: "provided", title: "Provided", hint: "Pandas's own tools and the workspace's keys serve you here: nothing to do." },
] as const;

/**
 * My connections (security plan D2, "Connector model — DECIDED 2026-10-19"): the model your agents run on first, under
 * LLM (AIT-118: a model is not a tool), then every tool you may use, in three groups — the ones waiting for your own
 * login, the ones connected, and the ones the workspace provides.
 */
export function ConnectionsPage() {
  const { search } = useLocation();
  const [rows, setRows] = useState<ConnectionDTO[] | null>(null);
  const [claude, setClaude] = useState<ClaudeConnectionDTO | null>(null);
  const [tokenFor, setTokenFor] = useState<ConnectionDTO | null>(null);
  const [claudeKey, setClaudeKey] = useState(false);
  const [claudeSignIn, setClaudeSignIn] = useState(false);
  const [error, setError] = useState<string | null>(new URLSearchParams(search).get("oauth_error"));

  const load = () =>
    Promise.all([
      get<ConnectionDTO[]>("/api/me/connections").then(setRows),
      get<ClaudeConnectionDTO>("/api/me/claude").then(setClaude, () => setClaude(null)),
    ]).catch((e) => setError(e.message));
  useEffect(() => {
    void load();
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
    if (channel) channel.onmessage = () => void load();
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      channel?.close();
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };
  /** The tool's sign-in in a tab of its own, opened on the click itself so no blocker stops it, and cut from this page. */
  const signIn = (c: ConnectionDTO) => {
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    void run(async () => {
      try {
        const { url } = await post<{ url: string }>(`/api/me/connections/${c.connectorId}/oauth`);
        if (tab) tab.location.href = url;
        else location.href = url;
      } catch (e) {
        tab?.close();
        throw e;
      }
    });
  };

  const claudeRow = claude && claudeConnection(claude);
  return (
    <div>
      <PageHeader title="My connections" subtitle="The model your agents run on, and every tool they may use for you." />
      <div className="space-y-6 px-4 pb-8 md:px-6">
        <ErrorText error={error} />
        {claudeRow && (
          <List title="LLM" hint="The model your agents think with: everything they do for you runs on it.">
            <ConnectionRow c={claudeRow} onSignIn={() => setClaudeSignIn(true)} onToken={() => setClaudeKey(true)} onDisconnect={() => run(() => del("/api/me/claude"))} />
          </List>
        )}
        {rows === null && <p className="text-sm text-muted">Loading…</p>}
        {rows && rows.length === 0 && <p className="text-sm text-muted">No tool is open to you yet: an admin grants them in Users.</p>}
        {rows &&
          GROUPS.map((g) => {
            const inGroup = rows.filter((c) => c.group === g.key);
            if (!inGroup.length) return null;
            return (
              <List key={g.key} title={g.title} hint={g.hint}>
                {inGroup.map((c) => (
                  <ConnectionRow
                    key={c.connectorId}
                    c={c}
                    onSignIn={() => signIn(c)}
                    onToken={() => setTokenFor(c)}
                    onDisconnect={() => run(() => del(`/api/me/connections/${c.connectorId}`))}
                  />
                ))}
              </List>
            );
          })}
      </div>
      <TokenDialog
        connection={tokenFor}
        onClose={() => setTokenFor(null)}
        onTest={(key) => post<{ ok: boolean; status: number; account: string | null }>(`/api/me/connections/${tokenFor!.connectorId}/test`, { key })}
        onSave={async (key) => {
          await put(`/api/me/connections/${tokenFor!.connectorId}`, { key });
          setTokenFor(null);
          await load();
        }}
      />
      <ClaudeSignInDialog
        open={claudeSignIn}
        onClose={() => setClaudeSignIn(false)}
        onDone={(state) => {
          setClaude(state);
          setClaudeSignIn(false);
        }}
      />
      <TokenDialog
        connection={claudeKey && claudeRow ? claudeRow : null}
        onClose={() => setClaudeKey(false)}
        onSave={async (apiKey) => {
          setClaude(await put<ClaudeConnectionDTO>("/api/me/claude", { apiKey }));
          setClaudeKey(false);
        }}
      />
    </div>
  );
}

/**
 * Claude, the model your agents think with, drawn by the same row as a tool: your own Claude — a sign-in with your Claude plan,
 * kept by Claude Code itself in a Claude home that is yours alone, or your own Claude Console API key, reached through
 * the platform so it never enters a run — else the workspace's Claude.
 */
function claudeConnection(state: ClaudeConnectionDTO): ConnectionDTO {
  const connected = state.status === "connected";
  const needsYou = state.status === "lapsed" || (state.status === "not_connected" && state.ownRequired);
  const signedIn = state.kind === "subscription";
  return {
    connectorId: "claude", slug: "claude", name: "Claude", catalog: "claude",
    group: connected ? "connected" : needsYou ? "needs_you" : "provided",
    method: signedIn ? "oauth" : connected || state.status === "lapsed" ? "key" : "workspace",
    status: connected || state.status === "lapsed" ? state.status : state.ownRequired ? "not_connected" : "provided",
    signIn: state.signIn,
    token: {
      label: "Your Claude Console API key",
      placeholder: "sk-ant-…",
      steps: "console.anthropic.com → API keys → Create key (sk-ant-…). It is billed to your own Console account, kept encrypted, and never shown again or handed to a run.",
    },
    detail: state.status === "lapsed" ? `${state.detail ?? "Anthropic refused the key"}. Your messages wait until you reconnect.` : null,
    connectedAt: state.connectedAt, account: state.account, lastUsedAt: null,
    access: connected ? null : "AgentOS's own Claude",
  };
}

function ConnectionRow({ c, onSignIn, onToken, onDisconnect }: { c: ConnectionDTO; onSignIn: () => void; onToken: () => void; onDisconnect: () => void }) {
  const model = c.connectorId === "claude";
  const subtitle =
    c.status === "impersonated" ? `As ${c.account ?? "you"} · the platform acts as you, with your own Google access`
      : c.status === "connected"
        ? [model ? (c.method === "oauth" ? `Signed in with Claude${c.account ? ` as ${c.account}` : ""}: your runs use your own Claude plan` : "Your runs spend your own Claude Console API key")
            : c.account ? `As ${c.account}` : c.method === "oauth" ? "Signed in" : "With your own token",
            c.lastUsedAt ? `last used ${shortDate(c.lastUsedAt)}` : c.connectedAt ? `connected ${shortDate(c.connectedAt)}` : null].filter(Boolean).join(" · ")
      : c.status === "lapsed" ? `Lapsed: ${c.detail ?? "the tool refused the login"}`
      : c.status === "provided" ? [c.method === "internal" ? "Connected through Pandas" : "Provided by the workspace", c.access, c.detail].filter(Boolean).join(" · ")
      : c.detail ?? (model ? "Your messages wait until you sign in with Claude or add your own Claude Console API key." : "Not connected: agents cannot use it for you until you do.");
  const reconnect = c.status === "lapsed" || c.status === "connected";
  const actions =
    c.group === "provided" ? (model ? (
      <span className="flex flex-wrap items-center justify-end gap-2">
        {c.signIn && <Button size="sm" onClick={onSignIn}>Sign in with Claude</Button>}
        <Button size="sm" variant="ghost" onClick={onToken}>Use my own key</Button>
      </span>
    ) : null) : (
      <span className="flex flex-wrap items-center justify-end gap-2">
        {c.status === "connected" && <Badge tone="emerald">Connected</Badge>}
        {c.status === "lapsed" && <Badge tone="red">Lapsed</Badge>}
        {c.signIn && (
          <Button size="sm" variant={c.status === "connected" ? "default" : "primary"} onClick={onSignIn}>
            {model ? (reconnect && c.method === "oauth" ? "Sign in again" : "Sign in with Claude") : reconnect ? "Reconnect" : "Connect"}
          </Button>
        )}
        {c.token && (!c.signIn || c.method === "key" || model) && (
          <Button size="sm" variant={c.signIn || c.status === "connected" ? "default" : "primary"} onClick={onToken}>
            {model ? (c.method === "key" && reconnect ? "Replace key" : "Use my own key") : c.status === "connected" ? "Replace token" : c.status === "lapsed" && c.method === "key" ? "Reconnect" : "Add a token"}
          </Button>
        )}
        {c.status !== "not_connected" && c.method !== "impersonation" && <Button size="sm" variant="ghost" onClick={onDisconnect}>Disconnect</Button>}
      </span>
    );
  return (
    <ListRow
      className={c.group === "provided" ? "bg-fill-subtle" : ""}
      leading={<span className={c.group === "provided" ? "opacity-60" : ""}><BrandTile brand={c.catalog ?? c.slug} /></span>}
      title={
        <>
          <span className={`truncate font-medium ${c.group === "provided" ? "text-muted" : ""}`}>{c.name}</span>
          {model && c.group === "needs_you" && <Badge tone="amber">Needs you</Badge>}
        </>
      }
      trailing={c.method === "impersonation" ? <Badge tone="emerald">Connected</Badge> : actions}
    >
      {/* Wraps rather than truncates: what to do about a tool has to be readable on a phone. */}
      <p className={`mt-0.5 text-sm ${c.group === "provided" ? "text-faint" : "text-muted"}`}>{subtitle}</p>
    </ListRow>
  );
}

/** A token of your own, pasted once with the tool's own steps beside it, tried before it is kept, and never shown again. */
function TokenDialog({ connection, onClose, onSave, onTest }: {
  connection: ConnectionDTO | null;
  onClose: () => void;
  onSave: (key: string) => Promise<void>;
  onTest?: (key: string) => Promise<{ ok: boolean; status: number; account: string | null }>;
}) {
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);
  const [busy, setBusy] = useState<"test" | "save" | null>(null);
  useEffect(() => {
    setKey("");
    setError(null);
    setTested(null);
  }, [connection?.connectorId]);
  const act = async (which: "test" | "save", fn: () => Promise<void>) => {
    setBusy(which);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const token = connection?.token;
  return (
    <Dialog
      open={connection !== null}
      onClose={onClose}
      title={`Connect ${connection?.name ?? ""}`}
      busy={busy !== null}
      secondary={
        onTest && (
          <Button
            disabled={!key.trim() || busy !== null}
            onClick={() =>
              act("test", async () => {
                const r = await onTest(key.trim());
                setTested(r.ok ? `Works${r.account ? `: ${r.account}` : ""}` : `${connection?.name} refused it (HTTP ${r.status})`);
              })
            }
          >
            {busy === "test" ? "Testing…" : "Test"}
          </Button>
        )
      }
      primary={
        <Button variant="primary" disabled={!key.trim() || busy !== null} onClick={() => act("save", () => onSave(key.trim()))}>
          {busy === "save" ? "Saving…" : "Save"}
        </Button>
      }
    >
      {token && <p className="text-sm text-muted">{token.steps}</p>}
      <Field label={token?.label ?? "Your token"} hint="Kept encrypted, used only for runs working for you, and never shown again.">
        <input
          type="password"
          autoComplete="off"
          className={`${inputCls} font-mono`}
          placeholder={token?.placeholder}
          value={key}
          onChange={(e) => {
            setKey(e.target.value);
            setTested(null);
          }}
        />
      </Field>
      {tested && <p className={`text-sm ${tested.startsWith("Works") ? "text-success" : "text-danger"}`}>{tested}</p>}
      <ErrorText error={error} />
    </Dialog>
  );
}

/**
 * Sign in with Claude: Claude Code's own sign-in, run for you in a Claude home that is yours alone. You sign in on
 * Claude's page in a new tab, and paste back the code it shows; Claude Code itself turns it into your login and keeps
 * it. The platform never sees your login, only that it worked and as which account.
 */
function ClaudeSignInDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (state: ClaudeConnectionDTO) => void }) {
  const [started, setStarted] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setStarted(false);
    setCode("");
    setError(null);
  }, [open]);
  const close = () => {
    if (started) void del("/api/me/claude/login").catch(() => {});
    onClose();
  };
  /** Claude's page in a tab of its own, opened on the click itself so no blocker stops it, and cut from this page. */
  const openSignIn = async () => {
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setBusy(true);
    setError(null);
    try {
      const { url } = await post<{ url: string }>("/api/me/claude/login");
      if (tab) tab.location.href = url;
      else window.open(url, "_blank", "noopener");
      setStarted(true);
    } catch (e: any) {
      tab?.close();
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await post<ClaudeConnectionDTO>("/api/me/claude/code", { code: code.trim() }));
    } catch (e: any) {
      setError(e.message);
      setStarted(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title="Sign in with Claude"
      description="Your agents then use your own Claude plan for everything they do for you."
      busy={busy}
      primary={
        <Button variant="primary" disabled={!started || !code.trim() || busy} onClick={finish}>
          {busy && started ? "Signing in…" : "Finish"}
        </Button>
      }
    >
      <ol className="list-decimal space-y-3 pl-5 text-sm text-muted">
        <li>
          <span>Open Claude's sign-in and sign in with your Claude account.</span>
          <div className="mt-2">
            <Button disabled={busy} onClick={openSignIn}>{started ? "Open it again" : "Open Claude's sign-in"}</Button>
          </div>
        </li>
        <li>
          <span>Claude shows you a code. Paste it here.</span>
          <Field label="Code" hint="Claude Code turns it into your login and keeps it in a Claude home that is yours alone; AgentOS never sees your login.">
            <input
              autoComplete="off"
              className={`${inputCls} font-mono`}
              disabled={!started}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        </li>
      </ol>
      <ErrorText error={error} />
    </Dialog>
  );
}

/** Where a sign-in in its own tab ends: it says so, tells My connections in the other tab, and closes itself where it may. */
export function ConnectionDonePage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const connected = params.get("connected");
  const failed = params.get("oauth_error");
  useEffect(() => {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(CHANNEL);
      channel.postMessage({ connected, failed });
      channel.close();
    }
    if (connected) setTimeout(() => window.close(), 1500);
  }, []);
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      {connected ? (
        <>
          <h1 className="text-heading font-semibold">{connected} is connected</h1>
          <p className="mt-2 text-sm text-muted">You can close this tab. My connections has updated.</p>
        </>
      ) : (
        <>
          <h1 className="text-heading font-semibold">The sign-in did not finish</h1>
          <p className="mt-2 text-sm text-danger">{failed ?? "Something went wrong."}</p>
          <p className="mt-2 text-sm text-muted">Close this tab and try Connect again on My connections.</p>
        </>
      )}
    </div>
  );
}
